import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { aiAgentApi } from "../lib/aiAgentApi";

const PERMISSION_META = [
    { key: "view_own_agenda",    label: "Ver própria agenda",     kind: "read",  icon: "fa-calendar" },
    { key: "view_all_agenda",    label: "Ver agenda geral",       kind: "read",  icon: "fa-calendar-days" },
    { key: "create_appointment", label: "Criar agendamentos",     kind: "write", icon: "fa-calendar-plus" },
    { key: "cancel_appointment", label: "Cancelar agendamentos",  kind: "write", icon: "fa-calendar-xmark" },
    { key: "view_financial",     label: "Ver financeiro",         kind: "read",  icon: "fa-coins" },
    { key: "view_clients",       label: "Buscar clientes",        kind: "read",  icon: "fa-users" },
    { key: "send_booking_link",  label: "Enviar link de agendamento", kind: "write", icon: "fa-paper-plane" },
    { key: "view_commissions",   label: "Ver comissões próprias", kind: "read",  icon: "fa-percent" },
    { key: "view_waitlist",      label: "Ver lista de espera",    kind: "read",  icon: "fa-hourglass-half" },
];

const ROLE_PRESETS = {
    owner:       { label: "Dono", all: true },
    barber:      { label: "Barbeiro",   perms: ["view_own_agenda", "create_appointment", "view_commissions"] },
    receptionist:{ label: "Secretária", perms: ["view_all_agenda", "create_appointment", "cancel_appointment", "view_clients", "send_booking_link"] },
    manager:     { label: "Gerente",    perms: ["view_own_agenda", "view_all_agenda", "create_appointment", "cancel_appointment", "view_clients", "send_booking_link", "view_commissions", "view_waitlist"] },
};

const emptyPermissions = () => PERMISSION_META.reduce((acc, p) => ({ ...acc, [p.key]: false }), {});
const permsFromPreset = (id) => {
    const preset = ROLE_PRESETS[id];
    if (!preset) return emptyPermissions();
    if (preset.all) return PERMISSION_META.reduce((acc, p) => ({ ...acc, [p.key]: true }), {});
    return PERMISSION_META.reduce((acc, p) => ({ ...acc, [p.key]: preset.perms.includes(p.key) }), {});
};

const maskPhone = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 13);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `+${d.slice(0,2)} ${d.slice(2)}`;
    if (d.length <= 6) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4)}`;
    if (d.length <= 11) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
    return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9,13)}`;
};

const AIAgentSection = () => {
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [knowledge, setKnowledge] = useState([]);
    const [internalUsers, setInternalUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingKb, setEditingKb] = useState(null);
    const [editingUser, setEditingUser] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, st, kb, iu] = await Promise.all([
                aiAgentApi.getSettings(),
                aiAgentApi.stats(),
                aiAgentApi.listKnowledge(),
                aiAgentApi.listInternal(),
            ]);
            setSettings(s);
            setStats(st);
            setKnowledge(kb);
            setInternalUsers(iu);
        } catch (e) {
            toast.error("Falha ao carregar Agente IA", { description: e.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const featureBlocked = settings && !settings.feature_enabled;

    if (loading || !settings) {
        return <div data-testid="ai-section" className="text-sm text-slate-500 py-10 text-center">Carregando Agente IA...</div>;
    }

    return (
        <div data-testid="ai-section" className="space-y-8">
            {featureBlocked && (
                <div data-testid="ai-plan-warning"
                     className="rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-100 px-4 py-3 text-sm flex items-center gap-3">
                    <i className="fa-solid fa-lock text-amber-300"></i>
                    <div>
                        <strong>Recurso Agente IA não disponível no seu plano atual.</strong>{" "}
                        Faça upgrade para Imperial ou Enterprise para ativar o atendimento automático 24h.
                    </div>
                </div>
            )}

            {/* SUB-SEÇÃO 1 — AGENTE IA (CLIENTES) */}
            <AgentSettings
                settings={settings}
                stats={stats}
                disabled={featureBlocked}
                onChange={async (patch) => {
                    try {
                        const fresh = await aiAgentApi.patchSettings(patch);
                        setSettings(fresh);
                        toast.success("Configuração salva");
                    } catch (e) { toast.error("Falha", { description: e.message }); }
                }}
            />

            {/* SUB-SEÇÃO 2 — BASE DE CONHECIMENTO */}
            <KnowledgeSection
                items={knowledge}
                onNew={() => setEditingKb({ id: null, title: "", content: "" })}
                onEdit={(b) => setEditingKb(b)}
                onDelete={async (b) => {
                    if (!window.confirm(`Excluir bloco "${b.title}"?`)) return;
                    try { await aiAgentApi.deleteKnowledge(b.id); toast.success("Bloco excluído"); await load(); }
                    catch (e) { toast.error("Falha", { description: e.message }); }
                }}
            />

            {/* SUB-SEÇÃO 3 — NÚMEROS INTERNOS */}
            <InternalUsersSection
                items={internalUsers}
                onNew={() => setEditingUser({ id: null, name: "", phone: "", role_name: "Personalizado",
                    professional_id: null, active: true, permissions: emptyPermissions() })}
                onEdit={(u) => setEditingUser(u)}
                onToggle={async (u) => {
                    try { await aiAgentApi.toggleInternal(u.id); toast.success(u.active ? "Desativado" : "Ativado"); await load(); }
                    catch (e) { toast.error("Falha", { description: e.message }); }
                }}
                onDelete={async (u) => {
                    if (!window.confirm(`Excluir ${u.name}?`)) return;
                    try { await aiAgentApi.deleteInternal(u.id); toast.success("Excluído"); await load(); }
                    catch (e) { toast.error("Falha", { description: e.message }); }
                }}
            />

            {editingKb && (
                <KnowledgeEditor
                    block={editingKb}
                    onClose={() => setEditingKb(null)}
                    onSaved={async () => { setEditingKb(null); await load(); }}
                />
            )}
            {editingUser && (
                <InternalUserEditor
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onSaved={async () => { setEditingUser(null); await load(); }}
                />
            )}
        </div>
    );
};

// ─── AGENTE IA (SUB-SEÇÃO 1) ───────────────────────────────────────────────
const AgentSettings = ({ settings, stats, disabled, onChange }) => {
    const [assistantName, setAssistantName] = useState(settings.assistant_name || "");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testMessage, setTestMessage] = useState("Oi! Vocês têm horário para corte amanhã de manhã?");

    const runTest = async () => {
        setTesting(true);
        try {
            const r = await aiAgentApi.test({ message: testMessage, simulate_as: "client" });
            setTestResult(r);
        } catch (e) {
            toast.error("Falha ao testar", { description: e.message });
        } finally { setTesting(false); }
    };

    return (
        <section data-testid="ai-settings-section"
                 className="rounded-2xl border border-white/5 bg-black/40 p-5 space-y-4">
            <header className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-robot text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="salon-heading text-xl text-white">Agente IA de Atendimento</h3>
                    <p className="text-xs text-slate-400">Responde clientes automaticamente 24h por dia via WhatsApp.</p>
                </div>
            </header>

            <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/5 bg-black/40 p-3 flex items-center gap-3">
                    <button
                        type="button"
                        data-testid="ai-toggle"
                        disabled={disabled}
                        onClick={() => onChange({ ai_agent_enabled: !settings.ai_agent_enabled })}
                        className={`h-9 min-w-[68px] rounded-full border transition-all inline-flex items-center px-1 ${
                            settings.ai_agent_enabled
                                ? "bg-emerald-500/30 border-emerald-400/60 justify-end"
                                : "bg-slate-800 border-white/10 justify-start"
                        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                        <span className={`h-7 w-7 rounded-full ${settings.ai_agent_enabled ? "bg-emerald-400" : "bg-slate-500"}`}></span>
                    </button>
                    <div className="min-w-0">
                        <div className="text-sm text-white font-semibold">
                            {settings.ai_agent_enabled ? "IA ativa" : "IA desativada"}
                        </div>
                        <div className="text-[11px] text-slate-500">
                            {settings.ai_agent_enabled ? "Responde clientes automaticamente." : "Ative para atender 24h."}
                        </div>
                    </div>
                </div>
                <div className="rounded-xl border border-white/5 bg-black/40 p-3">
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Nome do assistente</label>
                    <input
                        data-testid="ai-assistant-name"
                        value={assistantName}
                        onChange={(e) => setAssistantName(e.target.value)}
                        onBlur={() => assistantName !== settings.assistant_name && onChange({ assistant_name: assistantName })}
                        disabled={disabled}
                        className="w-full h-9 px-3 rounded-lg border border-white/10 bg-slate-950/70 text-sm text-white disabled:opacity-50 focus:border-amber-500/50 focus:outline-none"
                        placeholder="ex: Vintage Assistente"
                    />
                </div>
            </div>

            {/* Simulador de teste rápido (via API, sem WhatsApp real) */}
            <div className="rounded-xl border border-sky-500/25 bg-sky-500/5 p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-sky-300/80">Testar agente (não envia WhatsApp real)</div>
                <div className="flex gap-2 flex-wrap">
                    <input
                        data-testid="ai-test-input"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        disabled={disabled || testing}
                        className="flex-1 min-w-[220px] h-9 px-3 rounded-lg border border-white/10 bg-slate-950/70 text-sm text-white disabled:opacity-50"
                        placeholder="Digite uma mensagem de cliente para testar..."
                    />
                    <button
                        data-testid="ai-test-btn"
                        onClick={runTest}
                        disabled={disabled || testing || !testMessage.trim()}
                        className="h-9 px-4 rounded-lg bg-sky-500/80 hover:bg-sky-500 text-white text-xs uppercase tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                        {testing ? <><i className="fa-solid fa-spinner fa-spin"></i>Testando...</> : <><i className="fa-solid fa-paper-plane"></i>Testar</>}
                    </button>
                </div>
                {testResult && (
                    <div data-testid="ai-test-result" className="mt-2 rounded-lg bg-slate-950/70 border border-white/5 p-3 text-sm text-slate-200 whitespace-pre-line">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                            Resposta ({testResult.tokens || 0} tokens · action: {testResult.parsed?.action || "none"})
                        </div>
                        {testResult.parsed?.message || testResult.raw}
                    </div>
                )}
            </div>

            {/* Estatísticas do mês */}
            {stats && (
                <div className="space-y-3" data-testid="ai-stats">
                    <TokenUsageBar
                        tokensUsed={stats.tokens_used}
                        quotaLimit={stats.quota_limit}
                        quotaPct={stats.quota_pct}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <StatChip icon="fa-comments" label="conversas atendidas" value={stats.conversations} />
                        <StatChip icon="fa-calendar-check" label="agendamentos criados pela IA" value={stats.appointments_created} />
                    </div>
                </div>
            )}
        </section>
    );
};

const fmtTokens = (n) => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) {
        const m = v / 1_000_000;
        return Number.isInteger(m) ? `${m}M` : `${m.toFixed(1)}M`;
    }
    return `${(v / 1000).toFixed(1)}k`;
};

const TokenUsageBar = ({ tokensUsed, quotaLimit, quotaPct }) => {
    const used = Number(tokensUsed) || 0;
    const limit = Number(quotaLimit) || 0;
    const pct = Math.min(100, Number.isFinite(quotaPct) ? quotaPct : (limit ? (used / limit) * 100 : 0));
    const remainingPct = 100 - pct;
    const lowQuota = limit > 0 && (limit - used) < limit * 0.1;

    const barColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
    const trackBorder = pct >= 90 ? "border-red-500/30" : pct >= 70 ? "border-amber-500/30" : "border-white/5";

    return (
        <div data-testid="ai-token-usage" className="rounded-xl border border-white/5 bg-black/40 p-3 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-1">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Uso de tokens este mês</span>
                <span data-testid="ai-token-usage-label" className="text-[11px] text-slate-300 font-mono">
                    {fmtTokens(used)} / {fmtTokens(limit)} tokens ({pct.toFixed(1)}%)
                </span>
            </div>
            <div className={`h-3 w-full rounded-full bg-slate-900 border ${trackBorder} overflow-hidden`}>
                <div
                    data-testid="ai-token-usage-fill"
                    className={`h-full rounded-full ${barColor} transition-all duration-500`}
                    style={{ width: `${Math.min(100, Math.max(pct > 0 ? 2 : 0, pct))}%` }}
                />
            </div>
            {lowQuota && (
                <div data-testid="ai-token-low-warning"
                     className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 text-xs px-3 py-2">
                    <i className="fa-solid fa-triangle-exclamation shrink-0"></i>
                    <span>Créditos de IA quase esgotados — restam {remainingPct.toFixed(1)}% da quota mensal.</span>
                </div>
            )}
        </div>
    );
};

const StatChip = ({ icon, label, value }) => (
    <div className="rounded-xl border border-white/5 bg-black/40 p-3">
        <div className="text-lg text-white font-semibold">{value}</div>
        <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
            <i className={`fa-solid ${icon} text-amber-300/70`}></i>{label}
        </div>
    </div>
);

// ─── BASE DE CONHECIMENTO (SUB-SEÇÃO 2) ────────────────────────────────────
const KB_SUGGESTIONS = [
    { icon: "fa-location-dot", label: "Endereço" },
    { icon: "fa-gift", label: "Promoções" },
    { icon: "fa-credit-card", label: "Formas de pagamento" },
    { icon: "fa-question-circle", label: "Perguntas frequentes" },
    { icon: "fa-square-parking", label: "Estacionamento" },
];

const KnowledgeSection = ({ items, onNew, onEdit, onDelete }) => (
    <section data-testid="ai-knowledge-section"
             className="rounded-2xl border border-white/5 bg-black/40 p-5 space-y-4">
        <header className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-book text-lg"></i>
                </div>
                <div>
                    <h3 className="salon-heading text-xl text-white">Base de Conhecimento</h3>
                    <p className="text-xs text-slate-400">Blocos livres para a IA responder informações do seu salão.</p>
                </div>
            </div>
            <button
                data-testid="ai-kb-new-btn"
                onClick={onNew}
                className="h-9 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
            >
                <i className="fa-solid fa-plus"></i>Novo bloco
            </button>
        </header>

        <div className="flex flex-wrap gap-1.5 text-[11px] text-slate-500">
            <span className="text-slate-600">Sugestões:</span>
            {KB_SUGGESTIONS.map((s) => (
                <span key={s.label} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/5 bg-black/30">
                    <i className={`fa-solid ${s.icon} text-amber-300/70`}></i>{s.label}
                </span>
            ))}
        </div>

        {items.length === 0 ? (
            <div className="text-slate-500 text-sm py-8 text-center border border-white/5 rounded-xl">
                Nenhum bloco cadastrado. Adicione informações que a IA deve conhecer sobre seu salão.
            </div>
        ) : (
            <div className="space-y-2">
                {items.map((b) => (
                    <div key={b.id} data-testid={`ai-kb-item-${b.id}`}
                         className="rounded-xl border border-white/5 bg-black/40 p-3 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{b.title}</div>
                            <div className="text-[11px] text-slate-400 line-clamp-2 whitespace-pre-line">{b.content}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                            <button data-testid={`ai-kb-edit-${b.id}`} onClick={() => onEdit(b)}
                                    className="h-8 w-8 rounded-lg border border-white/10 text-slate-300 hover:text-amber-300 hover:border-amber-500/40 text-xs">
                                <i className="fa-solid fa-pen"></i>
                            </button>
                            <button data-testid={`ai-kb-delete-${b.id}`} onClick={() => onDelete(b)}
                                    className="h-8 w-8 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs">
                                <i className="fa-solid fa-trash"></i>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        )}
    </section>
);

const KnowledgeEditor = ({ block, onClose, onSaved }) => {
    const isNew = !block.id;
    const [title, setTitle] = useState(block.title || "");
    const [content, setContent] = useState(block.content || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!title.trim() || !content.trim()) {
            toast.error("Título e conteúdo são obrigatórios");
            return;
        }
        setSaving(true);
        try {
            if (isNew) await aiAgentApi.createKnowledge({ title, content });
            else await aiAgentApi.updateKnowledge(block.id, { title, content });
            toast.success(isNew ? "Bloco criado" : "Bloco atualizado");
            await onSaved();
        } catch (e) { toast.error("Falha", { description: e.message }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="ai-kb-modal">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-lg rounded-2xl border border-amber-500/30 bg-slate-950 p-6 space-y-4">
                <h3 className="salon-heading text-lg text-white">{isNew ? "Novo bloco" : "Editar bloco"}</h3>
                <input
                    data-testid="ai-kb-input-title"
                    value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="Título (ex: Endereço e localização)"
                    className="w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                />
                <textarea
                    data-testid="ai-kb-input-content"
                    value={content} onChange={(e) => setContent(e.target.value)}
                    placeholder="Conteúdo livre — o que a IA precisa saber sobre este tópico..."
                    rows={6}
                    className="w-full px-3 py-2 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none resize-y"
                />
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={saving}
                            className="h-10 px-4 rounded-lg border border-white/10 text-slate-300 text-xs uppercase tracking-widest hover:bg-white/5 disabled:opacity-50">
                        Cancelar
                    </button>
                    <button data-testid="ai-kb-save-btn" onClick={save} disabled={saving}
                            className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
                        {saving ? "Salvando..." : "Salvar bloco"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── NÚMEROS INTERNOS (SUB-SEÇÃO 3) ────────────────────────────────────────
const roleIcon = (role) => {
    const r = (role || "").toLowerCase();
    if (r.includes("dono") || r.includes("owner")) return "fa-crown";
    if (r.includes("barbeir")) return "fa-scissors";
    if (r.includes("secret") || r.includes("recep")) return "fa-headset";
    if (r.includes("gerente") || r.includes("manager")) return "fa-user-tie";
    return "fa-user";
};

const InternalUsersSection = ({ items, onNew, onEdit, onToggle, onDelete }) => (
    <section data-testid="ai-internal-section"
             className="rounded-2xl border border-white/5 bg-black/40 p-5 space-y-4">
        <header className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-mobile-screen text-lg"></i>
                </div>
                <div>
                    <h3 className="salon-heading text-xl text-white">Números Internos via WhatsApp</h3>
                    <p className="text-xs text-slate-400">Cadastre a equipe para gerenciar o sistema pelo próprio WhatsApp.</p>
                </div>
            </div>
            <button data-testid="ai-user-new-btn" onClick={onNew}
                    className="h-9 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
                <i className="fa-solid fa-plus"></i>Cadastrar número
            </button>
        </header>

        {items.length === 0 ? (
            <div className="text-slate-500 text-sm py-8 text-center border border-white/5 rounded-xl">
                Nenhum número interno cadastrado.
            </div>
        ) : (
            <div className="space-y-2">
                {items.map((u) => {
                    const activeCount = Object.values(u.permissions || {}).filter(Boolean).length;
                    return (
                        <div key={u.id} data-testid={`ai-user-row-${u.id}`}
                             className={`rounded-xl border p-3 flex items-center gap-3 ${
                                 u.active ? "border-white/5 bg-black/40" : "border-white/5 bg-black/20 opacity-60"
                             }`}>
                            <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center shrink-0">
                                <i className={`fa-solid ${roleIcon(u.role_name)}`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-white truncate">{u.name}</span>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{maskPhone(u.phone)}</span>
                                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
                                        u.active
                                            ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                            : "border-slate-500/40 text-slate-400 bg-slate-500/10"
                                    }`}>
                                        {u.active ? "Ativo" : "Inativo"}
                                    </span>
                                </div>
                                <div className="text-[11px] text-slate-400 mt-0.5">
                                    {u.role_name} · {activeCount} permissõe{activeCount !== 1 ? "s" : ""}
                                </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button data-testid={`ai-user-edit-${u.id}`} onClick={() => onEdit(u)}
                                        className="h-8 w-8 rounded-lg border border-white/10 text-slate-300 hover:text-amber-300 hover:border-amber-500/40 text-xs">
                                    <i className="fa-solid fa-pen"></i>
                                </button>
                                <button data-testid={`ai-user-toggle-${u.id}`} onClick={() => onToggle(u)}
                                        className={`h-8 w-8 rounded-lg border text-xs ${
                                            u.active
                                                ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                                                : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                        }`}>
                                    <i className={`fa-solid ${u.active ? "fa-pause" : "fa-play"}`}></i>
                                </button>
                                <button data-testid={`ai-user-delete-${u.id}`} onClick={() => onDelete(u)}
                                        className="h-8 w-8 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs">
                                    <i className="fa-solid fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        )}
    </section>
);

const InternalUserEditor = ({ user, onClose, onSaved }) => {
    const isNew = !user.id;
    const [form, setForm] = useState({
        name: user.name || "",
        phone: user.phone ? maskPhone(user.phone) : "",
        role_name: user.role_name || "Personalizado",
        active: user.active !== false,
        permissions: user.permissions || emptyPermissions(),
    });
    const [saving, setSaving] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setPerm = (key, val) => set({ permissions: { ...form.permissions, [key]: val } });

    const applyPreset = (id) => {
        const preset = ROLE_PRESETS[id];
        if (!preset) return;
        set({ role_name: preset.label, permissions: permsFromPreset(id) });
    };

    const save = async () => {
        const phoneNorm = String(form.phone).replace(/\D/g, "");
        if (!form.name.trim() || phoneNorm.length < 10) {
            toast.error("Nome e telefone são obrigatórios");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: phoneNorm,
                role_name: form.role_name,
                active: form.active,
                permissions: form.permissions,
            };
            if (isNew) await aiAgentApi.createInternal(payload);
            else await aiAgentApi.updateInternal(user.id, payload);
            toast.success(isNew ? "Número cadastrado" : "Atualizado");
            await onSaved();
        } catch (e) { toast.error("Falha", { description: e.message }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="ai-user-modal">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-amber-500/30 bg-slate-950 p-6 space-y-5">
                <h3 className="salon-heading text-lg text-white">{isNew ? "Cadastrar número interno" : `Editar ${user.name}`}</h3>

                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Nome</label>
                        <input data-testid="ai-user-input-name" value={form.name} onChange={(e) => set({ name: e.target.value })}
                               className="w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Telefone (com DDI)</label>
                        <input data-testid="ai-user-input-phone" value={form.phone} onChange={(e) => set({ phone: maskPhone(e.target.value) })}
                               placeholder="+55 (11) 98765-4321"
                               className="w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white font-mono focus:border-amber-500/50 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Cargo / Perfil</label>
                        <select data-testid="ai-user-input-role" value={form.role_name} onChange={(e) => set({ role_name: e.target.value })}
                                className="w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none">
                            {["Dono", "Barbeiro", "Secretária", "Gerente", "Personalizado"].map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Status</label>
                        <button type="button" data-testid="ai-user-toggle-active"
                                onClick={() => set({ active: !form.active })}
                                className={`w-full h-10 rounded-lg border text-xs font-semibold flex items-center justify-center gap-2 ${
                                    form.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-black/30 text-slate-400"
                                }`}>
                            <i className={`fa-solid ${form.active ? "fa-toggle-on" : "fa-toggle-off"}`}></i>
                            {form.active ? "Ativo" : "Inativo"}
                        </button>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-amber-300/70 font-bold border-b border-white/5 pb-2">Atalhos de perfil</div>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.entries(ROLE_PRESETS).map(([id, preset]) => (
                            <button key={id} type="button" data-testid={`ai-user-preset-${id}`} onClick={() => applyPreset(id)}
                                    className="h-8 px-3 rounded-lg border border-white/10 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-amber-300/70 font-bold border-b border-white/5 pb-2">Permissões</div>
                    <div className="grid sm:grid-cols-2 gap-1.5">
                        {PERMISSION_META.map((p) => (
                            <label key={p.key} data-testid={`ai-user-perm-${p.key}`}
                                   className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-all ${
                                       form.permissions[p.key]
                                           ? "border-amber-500/50 bg-amber-500/10 text-amber-100"
                                           : "border-white/5 bg-black/30 text-slate-300 hover:border-white/15"
                                   }`}>
                                <input type="checkbox" checked={!!form.permissions[p.key]}
                                       onChange={(e) => setPerm(p.key, e.target.checked)}
                                       className="accent-amber-500 h-4 w-4" />
                                <i className={`fa-solid ${p.icon} w-4 text-center text-amber-300/80`}></i>
                                <span className="text-xs flex-1">{p.label}</span>
                                <span className={`text-[9px] uppercase tracking-widest ${p.kind === "read" ? "text-sky-300/70" : "text-emerald-300/70"}`}>
                                    {p.kind === "read" ? "Consulta" : "Ação"}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
                    <button onClick={onClose} disabled={saving}
                            className="h-10 px-4 rounded-lg border border-white/10 text-slate-300 text-xs uppercase tracking-widest hover:bg-white/5 disabled:opacity-50">
                        Cancelar
                    </button>
                    <button data-testid="ai-user-save-btn" onClick={save} disabled={saving}
                            className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
                        {saving ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIAgentSection;
